import { number, NumberSchema, string, StringSchema } from 'yup'
import type { InputWithValidation } from '../../../types/EventDetailTypes'
import { constructStandardValidationMessage } from './constructStandardValidationMessage'
import { StandardValidationConstraintKeys } from '../_constants/standard-validation-constraint-keys'
import type { SchemaEntryType } from '../_types/UseSchemaTypes'
import { logError } from '../../../../Helpers/logging'
import { exists } from '../../../../UtilityHelpers/exists'

const getEntryType = (field: InputWithValidation) => {
    switch (field.type) {
        case 'number':
        case 'range':
            return () =>
                //For now this does not allow optional number fields.
                number().typeError(constructStandardValidationMessage(StandardValidationConstraintKeys.VALID_NUMBER))
        case 'text':
        case 'textarea':
        case 'date':
        case 'datetime-local':
        case 'month':
        case 'time':
        case 'week':
        case 'email':
        case 'tel':
        case 'password':
        case 'url':
        case 'search':
        case 'image':
        case 'file':
        case 'color':
        case 'select':
        case 'select-one':
        case 'select-multiple':
        case 'radio':
        case 'checkbox':
            return string
        case 'hidden':
        case 'fieldset':
        case 'submit':
            return null
        default:
            logError(
                `${field.type} has not been integrated in useSchema's auto schema generation, please write this functionality first.`
            )
            return null
    }
}

const numberSchemaRules = (schemaEntry: SchemaEntryType, field: InputWithValidation): SchemaEntryType => {
    if (!(schemaEntry instanceof NumberSchema)) {
        return schemaEntry
    }
    const input = field as HTMLInputElement
    let schema = schemaEntry as NumberSchema
    if (exists(input.min) && !isNaN(parseFloat(input.min)))
        schema = schema.min(
            parseFloat(input.min),
            constructStandardValidationMessage(StandardValidationConstraintKeys.MIN, input.min)
        )
    if (exists(input.max) && !isNaN(parseFloat(input.max)))
        schema = schema.max(
            parseFloat(input.max),
            constructStandardValidationMessage(StandardValidationConstraintKeys.MAX, input.max)
        )
    return schema
}
const stringSchemaRules = (schemaEntry: SchemaEntryType, field: InputWithValidation): SchemaEntryType => {
    if (!(schemaEntry instanceof StringSchema)) {
        return schemaEntry
    }
    if (field instanceof HTMLSelectElement) {
        // TODO: remove this if statement when Select has been implemented by useSchema
        // see OPGAVES-2081
        return schemaEntry
    }
    const input = field as HTMLInputElement | HTMLTextAreaElement
    let schema = schemaEntry as StringSchema
    if (exists(input.minLength) && field.minLength >= 0)
        schema = schema.min(
            field.minLength,
            constructStandardValidationMessage(StandardValidationConstraintKeys.MIN_LENGTH, field.minLength)
        )
    if (exists(input.maxLength) && field.maxLength >= 0)
        schema = schema.max(
            field.maxLength,
            constructStandardValidationMessage(StandardValidationConstraintKeys.MAX_LENGTH, field.maxLength)
        )
    if (exists((input as HTMLInputElement).pattern))
        schema =
            (schema as StringSchema).matches?.(new RegExp((input as HTMLInputElement).pattern), {
                message: constructStandardValidationMessage(StandardValidationConstraintKeys.PATTERN),
            }) ?? schema
    return schema
}

/**
 * @memberof generateSchema
 * */
export const generateSchemaEntry = (field: InputWithValidation): SchemaEntryType | null => {
    let schemaEntry
    schemaEntry = getEntryType(field)?.() as StringSchema | NumberSchema
    if (!schemaEntry) return null
    if (field.required)
        schemaEntry = schemaEntry.required(
            constructStandardValidationMessage(StandardValidationConstraintKeys.REQUIRED)
        )
    schemaEntry = numberSchemaRules(schemaEntry, field)
    schemaEntry = stringSchemaRules(schemaEntry, field)
    return schemaEntry as SchemaEntryType
}
